import { Appointment } from '../types';

export interface TimeSlot {
  date: string;
  time: string;
  providerId?: string;
  room?: string;
}

export interface ConflictInfo {
  hasConflict: boolean;
  conflictType?: 'provider' | 'room' | 'both';
  conflictingAppointment?: Appointment;
  message?: string;
}

/**
 * Check if two time ranges overlap
 */
export const doTimesOverlap = (
  start1: Date,
  end1: Date,
  start2: Date,
  end2: Date
): boolean => {
  return start1 < end2 && end1 > start2;
};

/**
 * Parse appointment date and time into Date objects
 */
export const getAppointmentTimeRange = (appt: Appointment | Omit<Appointment, 'id' | 'startTime'>) => {
  const startTime = new Date(`${appt.date}T${appt.time}`);
  const endTime = new Date(startTime.getTime() + appt.duration * 60000);
  return { startTime, endTime };
};

/**
 * Check for appointment conflicts (double-booking)
 */
export const checkAppointmentConflict = (
  newAppt: Omit<Appointment, 'id' | 'startTime'>,
  existingAppointments: Appointment[],
  excludeApptId?: string
): ConflictInfo => {
  const { startTime: newStart, endTime: newEnd } = getAppointmentTimeRange(newAppt);

  for (const existingAppt of existingAppointments) {
    // Skip the appointment being edited
    if (excludeApptId && existingAppt.id === excludeApptId) {
      continue;
    }

    // Skip cancelled or no-show appointments
    if (existingAppt.status === 'CANCELLED' || existingAppt.status === 'NO_SHOW') {
      continue;
    }

    const { startTime: existingStart, endTime: existingEnd } = getAppointmentTimeRange(existingAppt);

    if (!doTimesOverlap(newStart, newEnd, existingStart, existingEnd)) {
      continue;
    }

    // Check for provider conflict
    const providerConflict = newAppt.providerId === existingAppt.providerId;

    // Check for room conflict
    const roomConflict = newAppt.room === existingAppt.room;

    if (providerConflict && roomConflict) {
      return {
        hasConflict: true,
        conflictType: 'both',
        conflictingAppointment: existingAppt,
        message: `Both Dr. ${existingAppt.providerId} and ${existingAppt.room} are already booked from ${existingAppt.time} to ${getEndTime(existingAppt)}`
      };
    } else if (providerConflict) {
      return {
        hasConflict: true,
        conflictType: 'provider',
        conflictingAppointment: existingAppt,
        message: `Dr. ${existingAppt.providerId} is already booked from ${existingAppt.time} to ${getEndTime(existingAppt)}`
      };
    } else if (roomConflict) {
      return {
        hasConflict: true,
        conflictType: 'room',
        conflictingAppointment: existingAppt,
        message: `${existingAppt.room} is already booked from ${existingAppt.time} to ${getEndTime(existingAppt)}`
      };
    }
  }

  return { hasConflict: false };
};

/**
 * Get the end time of an appointment as a string
 */
export const getEndTime = (appt: Appointment | Omit<Appointment, 'id' | 'startTime'>): string => {
  const { endTime } = getAppointmentTimeRange(appt);
  return endTime.toTimeString().slice(0, 5);
};

/**
 * Find available time slots
 */
export const findAvailableSlots = (
  date: string,
  providerId: string,
  room: string,
  duration: number,
  existingAppointments: Appointment[],
  startHour = 8,
  endHour = 18,
  slotInterval = 30
): TimeSlot[] => {
  const availableSlots: TimeSlot[] = [];
  const dayAppointments = existingAppointments.filter(
    appt => appt.date === date && appt.status !== 'CANCELLED' && appt.status !== 'NO_SHOW'
  );

  for (let hour = startHour; hour < endHour; hour++) {
    for (let minute = 0; minute < 60; minute += slotInterval) {
      const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;

      const testAppt: Omit<Appointment, 'id' | 'startTime'> = {
        date,
        time,
        duration,
        providerId,
        room,
        patientId: '',
        type: '',
        status: 'SCHEDULED',
        internalNotes: '',
        patientNotes: '',
        isWalkIn: false
      };

      const conflict = checkAppointmentConflict(testAppt, dayAppointments);

      if (!conflict.hasConflict) {
        availableSlots.push({ date, time, providerId, room });
      }
    }
  }

  return availableSlots;
};

/**
 * Suggest next available slots when there's a conflict
 */
export const suggestAlternativeSlots = (
  requestedDate: string,
  providerId: string,
  room: string,
  duration: number,
  existingAppointments: Appointment[],
  numberOfSuggestions = 5
): TimeSlot[] => {
  const suggestions: TimeSlot[] = [];
  let currentDate = new Date(requestedDate);

  // Try the requested date first
  const sameDaySlots = findAvailableSlots(
    requestedDate,
    providerId,
    room,
    duration,
    existingAppointments
  );

  suggestions.push(...sameDaySlots.slice(0, numberOfSuggestions));

  // If not enough slots, try next few days
  let daysChecked = 0;
  while (suggestions.length < numberOfSuggestions && daysChecked < 14) {
    currentDate.setDate(currentDate.getDate() + 1);
    const dateStr = currentDate.toISOString().split('T')[0];

    const slots = findAvailableSlots(
      dateStr,
      providerId,
      room,
      duration,
      existingAppointments
    );

    suggestions.push(...slots.slice(0, numberOfSuggestions - suggestions.length));
    daysChecked++;
  }

  return suggestions.slice(0, numberOfSuggestions);
};

/**
 * Format time slot for display
 */
export const formatTimeSlot = (slot: TimeSlot): string => {
  const date = new Date(slot.date);
  const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
  const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return `${dayName}, ${dateStr} at ${slot.time}`;
};
